import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  AppRoutes,
  Contributor,
  LabelRelation,
  PrismicCollectionDetailField,
  PrismicCollectionDetails,
} from "types";
import { CollectionDetailsForm } from "./form";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "contexts/AuthContext";
import {
  getPrismicCollectionDocument,
  getPrismicTaskInstructions,
  getWorkTaskDocumentId,
  getWorkTaskIconUrl,
  stripHtmlTags,
  renderStructuredText,
} from "lib/prismic";
import { captureDB } from "lib/indexed-db";
import { getLabelRelations } from "lib/label-relations";
import { parseKeyValueLines } from "lib/key-value-lines";

export type DeviceLabel = {
  key: string;
  label: string;
  camera_not_flipped: boolean;
};
export type OtherLabel = {
  title: string;
  required: boolean;
  labels: Record<string, string>;
  field_type?: string; // For task-level fields: 'text', 'numeric', 'date', 'dropdown'
  related_to?: string;
  qr_prefill?: boolean;
};

export function getCollectionDetailLabels(
  fields: PrismicCollectionDetailField[]
): Record<string, OtherLabel> {
  return Object.fromEntries(
    fields.flatMap((field) => {
      if (field.use_as_step_labels) return [];
      const id = field.label_id?.toString().trim();
      if (!id) return [];

      const labels: Record<string, string> = {};
      if (field.label_type === "dropdown" && field.dropdown_options) {
        parseKeyValueLines(field.dropdown_options).forEach(({ key, value }) => {
          if (key && value) labels[key] = value;
        });
      }

      return [
        [
          id,
          {
            title: field.label_title?.toString() || id,
            required: Boolean(field.is_required),
            labels,
            field_type: field.label_type || "text",
            related_to: field._related_label_id,
            qr_prefill: field.qr_prefill === true,
          },
        ],
      ];
    })
  );
}

export interface Config {
  selectedCollection?: PrismicCollectionDetails;
  collection_title?: string;
  collection_icon?: string | null;
  collection_intro?: string;
  collection_image?: string | null;
  labels?: {
    devices: Array<DeviceLabel>;
  } & Record<string, OtherLabel>;
  tasks: Record<
    string,
    {
      enabled: boolean;
      capture_type: string;
      task_name: string;
      task_id: string;
      submissions_per_session: number;
      workTaskDocumentId?: string;
      workTaskIconUrl?: string;
    }
  >;
  collection_id?: string;
  labelRelations?: LabelRelation[];
}

export interface CollectionDetailsContextInterface {
  contributor: Contributor;
  collectionDetails: Array<string> | undefined;
  resetCollectionDetails: () => void;
  setIsShow: (value: boolean) => void;
  config: Config | null | undefined;
  task?: string;
  markCollectionDetailsCompleted: () => void;
}

// Prismic serves images through imgix with lossy defaults (auto=compress,format),
// which blurs hard-edged content like QR codes. Serve as lossless PNG instead.
function sharpImageUrl(url: string | null | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    const parsed = new URL(url);
    // SVGs are already resolution-independent; forcing fm=png would rasterize them.
    if (parsed.pathname.toLowerCase().endsWith(".svg")) {
      return url;
    }
    parsed.searchParams.delete("auto");
    parsed.searchParams.set("fm", "png");
    return parsed.toString();
  } catch {
    return url;
  }
}

export const CollectionDetailsContext =
  createContext<CollectionDetailsContextInterface>(
    {} as CollectionDetailsContextInterface
  );

export const CollectionDetailsContextProvider = ({
  children,
}: PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [isShow, setIsShow] = useState<boolean>(false);
  const [contributor, setContributor] = useState<Contributor>("appen");
  const [config, setConfig] = useState<Config | undefined | null>();
  const [task, setTask] = useState<string | undefined>(undefined);
  const [collectionDetailsCompleted, setCollectionDetailsCompleted] =
    useState<boolean>(false);
  const [hasExistingCollectionDetails, setHasExistingCollectionDetails] =
    useState<boolean>(false);

  // Track previous taskId to detect actual task changes
  const prevTaskIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!token) {
      return;
    }
    const { contributor } = token;
    if (contributor) {
      setContributor(contributor);
    }
  }, [location.pathname, token]);

  useEffect(() => {
    if (!token?.contributor) {
      return;
    }

    // Get taskId from location state
    const taskId = location.state?.taskId;

    // Only reset collection details completed flag when task actually changes
    if (
      prevTaskIdRef.current !== undefined &&
      prevTaskIdRef.current !== taskId
    ) {
      setCollectionDetailsCompleted(false);
    }

    // Update the ref
    prevTaskIdRef.current = taskId;

    (async () => {
      const collection = await getPrismicCollectionDocument().catch((err) => {
        console.error("Error fetching collection details from Prismic", err);
        return null;
      });

      if (!collection) {
        return setConfig(null);
      }
      const res = collection.data;

      // Fetch task instructions if we have a taskId
      let taskInstructions = null;
      const selectedTask = (res.tasks || []).find(
        (candidate) => candidate.task_id?.toString() === taskId
      );
      const selectedWorkTaskDocumentId = getWorkTaskDocumentId(
        selectedTask?.work_task
      );
      if (taskId && !selectedWorkTaskDocumentId) {
        try {
          taskInstructions = await getPrismicTaskInstructions(
            taskId,
            collection
          );
        } catch (err) {
          console.warn("Could not fetch task instructions:", err);
        }
      }

      // Extract task-level collection details from slices
      const taskCollectionDetails =
        taskInstructions?.instruction_content
          ?.filter((slice: any) => slice.slice_type === "collection_details")
          ?.flatMap((slice: any) => slice.items || []) || [];

      // Convert task collection details to the same format as collection-level ones
      const taskLabels = getCollectionDetailLabels(taskCollectionDetails);

      setConfig({
        selectedCollection: collection,
        collection_title: res.collection_title
          ? stripHtmlTags(renderStructuredText(res.collection_title))
          : undefined,
        collection_icon: res.collection_icon?.url,
        collection_intro: res.collection_intro
          ? renderStructuredText(res.collection_intro)
          : undefined,
        collection_image: sharpImageUrl(res.collection_image?.url),
        labelRelations: getLabelRelations(
          (taskInstructions?.instruction_content || []) as any[]
        ),
        labels: {
          devices: res.devices
            .filter(({ key, label }) => key && label)
            .map(({ key, label, camera_not_flipped }) => ({
              key: key!.toString(),
              label: label!.toString(),
              camera_not_flipped,
            })),
          ...taskLabels,
        } as Config["labels"],

        tasks: Object.fromEntries(
          (res.tasks || [])
            .filter(({ task_id }) => task_id)
            .map(
              ({
                task_name,
                task_id,
                enabled,
                capture_type,
                submissions_per_session,
                work_task,
              }) => [
                task_id!.toString(),
                {
                  enabled: enabled || false,
                  capture_type: capture_type || "photo",
                  task_name: task_name?.toString() || task_id!.toString(),
                  task_id: task_id!.toString(),
                  submissions_per_session: parseInt(
                    submissions_per_session?.toString() || "5",
                    10
                  ),
                  workTaskDocumentId: getWorkTaskDocumentId(work_task),
                  workTaskIconUrl: getWorkTaskIconUrl(work_task),
                },
              ]
            )
        ),
      });
    })();
  }, [token, location.state?.taskId]);

  // Check if collection details already exist in IndexedDB step 0
  useEffect(() => {
    const checkExistingCollectionDetails = async () => {
      const taskId = location.state?.taskId;
      if (!taskId) return;

      try {
        const storedCaptures = await captureDB.getStepCaptures(taskId);
        const step0 = storedCaptures.find((s) => s.stepNumber === 0);
        setHasExistingCollectionDetails(!!step0?.collectionDetails);
      } catch (error) {
        setHasExistingCollectionDetails(false);
      }
    };

    checkExistingCollectionDetails();
  }, [location.state?.taskId]);

  useEffect(() => {
    const taskId = location.state?.taskId;

    if (
      taskId &&
      config &&
      [
        AppRoutes.capture,
        AppRoutes.batchUpload,
        AppRoutes.irisUpload,
        AppRoutes.pcrCapture,
        AppRoutes.orb,
        AppRoutes.stepCapture,
        AppRoutes.stepOrb,
        AppRoutes.stepRtms,
      ].includes(location.pathname as AppRoutes) &&
      config.labels &&
      Object.keys(config.labels).some((key) => key !== "devices") &&
      !collectionDetailsCompleted &&
      !hasExistingCollectionDetails
    ) {
      setIsShow(false);
      navigate(AppRoutes.taskInstructions, {
        replace: true,
        state: {
          ...location.state,
          taskId,
          nextRoute: location.pathname,
        },
      });
    }
  }, [
    config,
    location.state,
    location.pathname,
    collectionDetailsCompleted,
    hasExistingCollectionDetails,
    navigate,
  ]);

  const [collectionDetails, setCollectionDetails] = useState<
    Array<string> | undefined
  >(undefined);

  const resetCollectionDetails = useCallback(
    () => setCollectionDetails(undefined),
    []
  );

  const markCollectionDetailsCompleted = useCallback(() => {
    setCollectionDetailsCompleted(true);
  }, []);

  const handleCancel = useCallback(() => {
    // Always hide the modal first
    setIsShow(false);

    const taskId = location.state?.taskId;
    const taskName = location.state?.taskName;
    const captureType = location.state?.captureType;

    if (taskId) {
      // Return to the single setup page while preserving the task route.
      navigate(AppRoutes.taskInstructions, {
        state: {
          taskId,
          taskName,
          captureType,
          nextRoute: location.pathname,
        },
      });
    } else {
      // Otherwise navigate to home screen
      navigate(AppRoutes.home);
    }
  }, [location.pathname, location.state, navigate]);

  return (
    <CollectionDetailsContext.Provider
      value={{
        contributor,
        collectionDetails,
        resetCollectionDetails,
        setIsShow,
        config,
        task,
        markCollectionDetailsCompleted,
      }}
    >
      {isShow && (
        <CollectionDetailsForm
          onSubmit={(data) => {
            setIsShow(false);
            setCollectionDetails(data as unknown as any);
            setTask(data.task);
            setCollectionDetailsCompleted(true);
            setHasExistingCollectionDetails(true);
          }}
          setIsShow={handleCancel}
          contributor={contributor}
        />
      )}
      {!isShow && children}
    </CollectionDetailsContext.Provider>
  );
};
